import { getCampaignLeadCostQueryOptions } from "@/api/campaign.api";
import { creditsQueryOptions } from "@/api/credits.api";
import {
  ConfigureIntegrationForm,
  configureIntegrationFormSchema,
} from "@/components/campaign/setup/delivery/ConfigureIntegrationForm";
import { getCampaignUpdateCreditStats } from "@/services/credits.service";
import { Campaign } from "@/types/campaign";
import { useStepperSubmitHandlerState } from "@/utils/stepper";
import { createFileRoute, NavigateOptions } from "@tanstack/react-router";
import { useMemo } from "react";
import z from "zod";
import { Route as BuyCreditsRoute } from "../buy-credits";

const historyStateSchema = z
  .object({
    values: configureIntegrationFormSchema,
    autoSubmit: z.boolean(),
  })
  .optional()
  .catch(undefined);

function parseHistoryState(historyState: z.infer<typeof historyStateSchema>) {
  return historyStateSchema.parse(historyState);
}

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/delivery/setup/integration/configure",
)({
  component: RouteComponent,
  staticData: {
    crumb: "Opsætning",
    meta: {
      title: "Opsætning",
    },
  },
  beforeLoad: ({ location }) => {
    const historyState = parseHistoryState(
      location.state.routeStates?.[Route.id],
    );
    return {
      parseHistoryState,
      useHistoryState: () => historyState,
    };
  },
});

function RouteComponent() {
  const context = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const { data: campaign } = context.useCampaignQuery();

  const stepper = context.useDeliveryStepper();
  const deliveryContext = stepper.context.delivery;
  if (deliveryContext?.type !== "Integration") throw null;
  const historyState = context.useHistoryState();

  const exportSystem = deliveryContext.exportSystem;
  const exportMode = deliveryContext.exportMode;
  const defaultValues = useMemo(
    () => ({
      exportInput: deliveryContext.exportInput ?? {},
    }),
    [deliveryContext],
  );
  const values = useMemo(
    () => historyState?.values ?? defaultValues,
    [historyState, defaultValues],
  );

  const { updateCampaignDelivery, confirmUpdate } =
    context.useUpdateCampaignDelivery();

  const [submitHandler, setSubmitHandler] = useStepperSubmitHandlerState();

  context.deliveryStepper.useStepperSubmit(
    useMemo(
      () => ({
        ...submitHandler,
        text: "Gem",
      }),
      [submitHandler],
    ),
  );

  return (
    <ConfigureIntegrationForm
      exportSystem={exportSystem}
      exportMode={exportMode}
      defaultValues={defaultValues}
      values={values}
      updateValues={async (values) => {
        if (!historyState?.autoSubmit) {
          const confirmed = await confirmUpdate();
          if (!confirmed) return;
        }

        if (!historyState?.autoSubmit && campaign.status === "Active") {
          const campaignWithNewValues: Campaign = {
            ...campaign,
            completionEmail: null,
            exportSystem,
            exportMode,
            ...values,
          };

          const [credits, campaignLeadCost, updatedCampaignLeadCost] =
            await Promise.all([
              context.queryClient.ensureQueryData(creditsQueryOptions),
              context.queryClient.ensureQueryData(
                getCampaignLeadCostQueryOptions(campaign),
              ),
              context.queryClient.ensureQueryData(
                getCampaignLeadCostQueryOptions(campaignWithNewValues),
              ),
            ]);

          const {
            updatedCreditsNeededRestOfPeriod,
            updatedCreditsNeededNextPeriod,
          } = getCampaignUpdateCreditStats({
            credits,
            campaignLeadCost,
            updatedCampaignLeadCost,
          });

          const neededCredits = Math.max(
            updatedCreditsNeededRestOfPeriod,
            updatedCreditsNeededNextPeriod,
          );

          if (neededCredits) {
            const newHistoryState: ReturnType<typeof parseHistoryState> = {
              values,
              autoSubmit: false,
            };

            await navigate({
              replace: true,
              state: {
                routeStates: {
                  [Route.id]: newHistoryState,
                },
              },
            });

            await navigate({
              to: BuyCreditsRoute.to,
              state: {
                routeStates: {
                  [BuyCreditsRoute.id]: {
                    neededCredits,
                    continueNavigateOptions: {
                      to: Route.to,
                      state: {
                        routeStates: {
                          [Route.id]: {
                            ...newHistoryState,
                            autoSubmit: true,
                          },
                        },
                      },
                    } satisfies NavigateOptions,
                    skipNavigateOptions: {
                      to: Route.to,
                      state: {
                        routeStates: {
                          [Route.id]: {
                            ...newHistoryState,
                            autoSubmit: false,
                          },
                        },
                      },
                    } satisfies NavigateOptions,
                  },
                },
              },
            });
            return;
          }
        }

        await updateCampaignDelivery({
          type: "Integration",
          exportSystem,
          exportMode,
          ...values,
        });
      }}
      setSubmitHandler={setSubmitHandler}
      autoSubmit={historyState?.autoSubmit}
    />
  );
}
